import { useState, useEffect } from "react";
import {
  List,
  Typography,
  Button,
  InputNumber,
  message,
  Modal,
  Input,
  Spin,
  Result,
} from "antd";
import {
  ArrowRightOutlined,
  CreditCardOutlined,
  SyncOutlined,
  ArrowLeftOutlined,
  AppleOutlined,
  GoogleOutlined,
  MobileOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import AddCreditCardForm from "./AddCreditCardForm";
import { getCreditCard, deleteCreditCard } from "../../../api/CreditCardApi";
import { topUpWallet } from "../../../api/WalletApi";
import { CreditCardItem } from "./CreditCardItem";

const { Text, Title } = Typography;

// ─── Amount Screen ─────────────────────────────────────────────────────────────
const AmountScreen = ({ onNext, onBack }) => {
  const [amount, setAmount] = useState(0);

  return (
    <div style={{ maxWidth: 350, margin: "56px auto", textAlign: "center" }}>
      <ArrowLeftOutlined
        onClick={onBack}
        style={{
          position: "absolute",
          left: 18,
          top: 22,
          fontSize: 22,
          color: "#213C6F",
          cursor: "pointer",
        }}
      />
      <Title level={3} style={{ color: "#213C6F", marginBottom: 8 }}>
        Enter Amount
      </Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 20 }}>
        How much would you like to top up?
      </Text>
      <InputNumber
        min={1}
        style={{ width: "80%", marginBottom: 30, height: 48, fontSize: 20 }}
        value={amount}
        onChange={setAmount}
        prefix="CA$"
        size="large"
      />
      <Button
        type="primary"
        style={{
          background: "#021b79",
          borderRadius: "8px",
          width: "80%",
          height: 48,
          fontWeight: 600,
          fontSize: 16,
        }}
        onClick={() => {
          if (!amount || amount <= 0) {
            message.error("Please enter a valid amount");
            return;
          }
          onNext(amount);
        }}
      >
        Continue
      </Button>
    </div>
  );
};

// ─── Payment Method Screen ──────────────────────────────────────────────────────
const PaymentMethodScreen = ({ onBack, onChoose, amount }) => {
  const methods = [
    {
      icon: <AppleOutlined style={{ fontSize: 24, color: "#000" }} />,
      title: "Apple Pay",
      subtitle: "Pay with Touch ID or Face ID",
      key: "applepay",
      bg: "#000",
      textColor: "#fff",
    },
    {
      icon: <GoogleOutlined style={{ fontSize: 24, color: "#4285F4" }} />,
      title: "Google Pay",
      subtitle: "Fast & secure checkout",
      key: "gpay",
      bg: "#fff",
      textColor: "#213C6F",
      border: "1px solid #e0e0e0",
    },
    {
      icon: <MobileOutlined style={{ fontSize: 24, color: "#6B48FF" }} />,
      title: "UPI",
      subtitle: "Pay via any UPI app",
      key: "upi",
      bg: "#f3f0ff",
      textColor: "#213C6F",
    },
    {
      icon: <SyncOutlined style={{ fontSize: 24, color: "#213C6F" }} />,
      title: "Via Pay ID",
      subtitle: "Use your Pay ID",
      key: "payid",
      bg: "#fff",
      textColor: "#213C6F",
      border: "1px solid #e0e0e0",
    },
    {
      icon: <CreditCardOutlined style={{ fontSize: 24, color: "#213C6F" }} />,
      title: "Credit / Debit Card",
      subtitle: "Visa, Mastercard, Amex",
      key: "creditcard",
      bg: "#fff",
      textColor: "#213C6F",
      border: "1px solid #e0e0e0",
    },
  ];

  return (
    <div style={{ maxWidth: 420, margin: "0 auto", padding: "16px" }}>
      <div style={{ position: "relative", marginBottom: 24, paddingTop: 8 }}>
        <ArrowLeftOutlined
          onClick={onBack}
          style={{
            fontSize: 22,
            color: "#213C6F",
            cursor: "pointer",
          }}
        />
        <Title level={4} style={{ color: "#213C6F", margin: "12px 0 4px" }}>
          Choose Payment Method
        </Title>
        <Text type="secondary">
          Adding{" "}
          <Text strong>
            CA${Number(amount).toLocaleString("en-CA", { minimumFractionDigits: 2 })}
          </Text>{" "}
          to your wallet
        </Text>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {methods.map((item) => (
          <div
            key={item.key}
            onClick={() => onChoose(item.key)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px 18px",
              borderRadius: 14,
              background: item.bg,
              border: item.border || "none",
              cursor: "pointer",
              boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
              transition: "transform 0.15s",
            }}
            onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.98)")}
            onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: "rgba(255,255,255,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {item.icon}
              </div>
              <div>
                <Text
                  strong
                  style={{
                    color: item.key === "applepay" ? "#fff" : item.textColor,
                    fontSize: 15,
                    display: "block",
                  }}
                >
                  {item.title}
                </Text>
                <Text
                  style={{
                    color:
                      item.key === "applepay"
                        ? "rgba(255,255,255,0.7)"
                        : "#888",
                    fontSize: 12,
                  }}
                >
                  {item.subtitle}
                </Text>
              </div>
            </div>
            <ArrowRightOutlined
              style={{
                color: item.key === "applepay" ? "#fff" : "#213C6F",
                fontSize: 16,
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Mock Processing Modal ─────────────────────────────────────────────────────
const MockProcessingModal = ({ visible, method, amount, onSuccess, onCancel }) => {
  const [step, setStep] = useState("confirm"); // confirm | processing | done

  useEffect(() => {
    if (!visible) setStep("confirm");
  }, [visible]);

  const handleConfirm = () => {
    setStep("processing");
    setTimeout(() => setStep("done"), 2000);
  };

  const methodLabels = {
    applepay: "Apple Pay",
    gpay: "Google Pay",
    upi: "UPI",
    payid: "Pay ID",
  };

  const methodIcons = {
    applepay: <AppleOutlined style={{ fontSize: 40, color: "#000" }} />,
    gpay: <GoogleOutlined style={{ fontSize: 40, color: "#4285F4" }} />,
    upi: <MobileOutlined style={{ fontSize: 40, color: "#6B48FF" }} />,
    payid: <SyncOutlined style={{ fontSize: 40, color: "#213C6F" }} />,
  };

  const formattedAmount = `CA$${Number(amount).toLocaleString("en-CA", {
    minimumFractionDigits: 2,
  })}`;

  return (
    <Modal open={visible} footer={null} closable={false} centered width={320}>
      {step === "confirm" && (
        <div style={{ textAlign: "center", padding: "8px 0" }}>
          <div style={{ marginBottom: 16 }}>{methodIcons[method]}</div>
          <Title level={4} style={{ margin: "0 0 8px" }}>
            {methodLabels[method]}
          </Title>
          <Text type="secondary" style={{ display: "block", marginBottom: 20 }}>
            Confirm payment of{" "}
            <Text strong style={{ color: "#021b79" }}>
              {formattedAmount}
            </Text>
          </Text>
          <div style={{ display: "flex", gap: 10 }}>
            <Button block onClick={onCancel}>
              Cancel
            </Button>
            <Button
              block
              type="primary"
              style={{ background: "#021b79" }}
              onClick={handleConfirm}
            >
              Confirm
            </Button>
          </div>
        </div>
      )}

      {step === "processing" && (
        <div style={{ textAlign: "center", padding: "24px 0" }}>
          <Spin size="large" />
          <Title level={5} style={{ marginTop: 16, color: "#213C6F" }}>
            Processing Payment...
          </Title>
          <Text type="secondary">Please wait</Text>
        </div>
      )}

      {step === "done" && (
        <div style={{ textAlign: "center", padding: "8px 0" }}>
          <CheckCircleOutlined
            style={{ fontSize: 52, color: "#52c41a", marginBottom: 12 }}
          />
          <Title level={4} style={{ margin: "0 0 8px" }}>
            Payment Successful!
          </Title>
          <Text type="secondary" style={{ display: "block", marginBottom: 20 }}>
            {formattedAmount} added to your wallet via {methodLabels[method]}
          </Text>
          <Button
            type="primary"
            block
            style={{ background: "#021b79" }}
            onClick={onSuccess}
          >
            Done
          </Button>
        </div>
      )}
    </Modal>
  );
};

// ─── UPI Screen ────────────────────────────────────────────────────────────────
const UpiScreen = ({ onBack, amount, onSuccess }) => {
  const [upiId, setUpiId] = useState("");
  const [processing, setProcessing] = useState(false);
  const [done, setDone] = useState(false);

  const upiApps = [
    { name: "PhonePe", color: "#5f259f", initials: "PP" },
    { name: "GPay", color: "#4285F4", initials: "GP" },
    { name: "Paytm", color: "#00BAF2", initials: "PT" },
    { name: "BHIM", color: "#004C8F", initials: "BH" },
  ];

  const handlePay = () => {
    if (!upiId || !upiId.includes("@")) {
      message.error("Please enter a valid UPI ID (e.g. name@upi)");
      return;
    }
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      setDone(true);
    }, 2000);
  };

  const formattedAmount = `CA$${Number(amount).toLocaleString("en-CA", {
    minimumFractionDigits: 2,
  })}`;

  if (done) {
    return (
      <div style={{ textAlign: "center", padding: "60px 24px" }}>
        <CheckCircleOutlined style={{ fontSize: 64, color: "#52c41a" }} />
        <Title level={4} style={{ marginTop: 16 }}>
          Payment Successful!
        </Title>
        <Text type="secondary">{formattedAmount} added via UPI</Text>
        <br />
        <Button
          type="primary"
          style={{ marginTop: 24, background: "#021b79" }}
          onClick={onSuccess}
        >
          Done
        </Button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 380, margin: "0 auto", padding: 20 }}>
      <ArrowLeftOutlined
        onClick={onBack}
        style={{ fontSize: 22, color: "#213C6F", cursor: "pointer", marginBottom: 20 }}
      />
      <Title level={4} style={{ color: "#213C6F", marginBottom: 4 }}>
        Pay via UPI
      </Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
        Amount:{" "}
        <Text strong style={{ color: "#021b79" }}>
          {formattedAmount}
        </Text>
      </Text>

      {/* UPI App Icons */}
      <Text strong style={{ display: "block", marginBottom: 12, color: "#213C6F" }}>
        Choose UPI App
      </Text>
      <div style={{ display: "flex", gap: 16, marginBottom: 28 }}>
        {upiApps.map((app) => (
          <div
            key={app.name}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
              cursor: "pointer",
            }}
            onClick={() => message.info(`${app.name} selected (mock)`)}
          >
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 14,
                background: app.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontWeight: 700,
                fontSize: 14,
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
            >
              {app.initials}
            </div>
            <Text style={{ fontSize: 11, color: "#555" }}>{app.name}</Text>
          </div>
        ))}
      </div>

      {/* Manual UPI ID */}
      <Text strong style={{ display: "block", marginBottom: 8, color: "#213C6F" }}>
        Or enter UPI ID
      </Text>
      <Input
        placeholder="yourname@upi"
        value={upiId}
        onChange={(e) => setUpiId(e.target.value)}
        size="large"
        style={{ borderRadius: 10, marginBottom: 20 }}
        suffix={<MobileOutlined style={{ color: "#6B48FF" }} />}
      />

      <Button
        type="primary"
        block
        size="large"
        loading={processing}
        style={{ background: "#021b79", borderRadius: 10, height: 48, fontWeight: 600 }}
        onClick={handlePay}
      >
        {processing ? "Verifying..." : "Pay Now"}
      </Button>
    </div>
  );
};

// ─── Add Credit Card Screen ─────────────────────────────────────────────────────
const AddCreditCardScreen = ({ onBack, onAddCard, cards, onDeleteCard, onSelectCard }) => {
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [cvv, setCvv] = useState("");

  const handlePay = () => {
    if (!selectedCardId) {
      message.error("Please select a card");
      return;
    }
    if (!cvv || cvv.length < 3) {
      message.error("Please enter a valid CVV");
      return;
    }
    const selectedCard = cards.find((c) => c.id === selectedCardId);
    onSelectCard({ ...selectedCard, cvv });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: "#FAF9F7" }}>
      <div style={{ position: "relative", padding: "16px", background: "#FAF9F7", flexShrink: 0 }}>
        <ArrowLeftOutlined
          onClick={onBack}
          style={{ position: "absolute", left: 18, top: 18, fontSize: 22, color: "#213C6F", cursor: "pointer" }}
        />
        <Title level={3} style={{ margin: 0, paddingTop: 10, textAlign: "center", color: "#213C6F" }}>
          Saved Credit Cards
        </Title>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 120px" }}>
        <List
          dataSource={cards}
          renderItem={(card) => (
            <CreditCardItem
              key={card.id}
              card={card}
              selectedCardId={selectedCardId}
              setSelectedCardId={setSelectedCardId}
              onDeleteCard={onDeleteCard}
            />
          )}
        />
        {selectedCardId && (
          <div style={{ marginTop: 20, textAlign: "center" }}>
            <Input.Password
              placeholder="Enter CVV"
              maxLength={4}
              style={{ width: "80%", marginBottom: 15, borderRadius: 8 }}
              value={cvv}
              onChange={(e) => setCvv(e.target.value)}
            />
            <Button
              type="primary"
              block
              style={{ background: "#021b79", borderRadius: "8px", height: 45, fontWeight: 600 }}
              onClick={handlePay}
            >
              Pay
            </Button>
          </div>
        )}
      </div>

      <div style={{ background: "#FAF9F7", padding: "16px", borderTop: "1px solid #eee", marginBottom: "6rem" }}>
        <Button
          block
          type="default"
          style={{ borderRadius: "8px", height: 45, fontWeight: 600, fontSize: 16 }}
          onClick={onAddCard}
        >
          Add Credit Card
        </Button>
      </div>
    </div>
  );
};

// ─── Main WalletTopUp ───────────────────────────────────────────────────────────
const WalletTopUp = ({ onBack }) => {
  const [view, setView] = useState("amount");
  const [cards, setCards] = useState([]);
  const [amount, setAmount] = useState(0);
  const [mockModal, setMockModal] = useState({ visible: false, method: null });

  useEffect(() => {
    if (view === "creditcard") {
      (async () => {
        const creditCards = await getCreditCard();
        setCards(creditCards);
      })();
    }
  }, [view]);

  const handleCardAdded = async () => {
    message.success("Card Added!");
    setView("creditcard");
    const creditCards = await getCreditCard();
    setCards(creditCards);
  };

  const handleDeleteCard = async (cardId) => {
    await deleteCreditCard(cardId);
    const creditCards = await getCreditCard();
    setCards(creditCards);
  };

  const handleCardSelect = async (card) => {
    try {
      await topUpWallet({ amount, paymentMethod: "CREDIT_CARD" });
      message.success(
        `CA$${Number(amount).toLocaleString("en-CA", { minimumFractionDigits: 2 })} successfully added via ${card.name}`
      );
      onBack();
    } catch {
      message.error("Payment failed, please try again");
    }
  };

  const handleChooseMethod = (method) => {
    if (["applepay", "gpay", "payid"].includes(method)) {
      setMockModal({ visible: true, method });
    } else {
      setView(method); // creditcard or upi
    }
  };

  const handleMockSuccess = async () => {
    try {
      await topUpWallet({ amount, paymentMethod: mockModal.method.toUpperCase() });
    } catch {
      // mock — ignore API errors
    }
    setMockModal({ visible: false, method: null });
    message.success(
      `CA$${Number(amount).toLocaleString("en-CA", { minimumFractionDigits: 2 })} added to your wallet!`
    );
    onBack();
  };

  if (view === "amount") {
    return (
      <AmountScreen
        onNext={(amt) => { setAmount(amt); setView("menu"); }}
        onBack={onBack}
      />
    );
  }

  if (view === "menu") {
    return (
      <>
        <PaymentMethodScreen
          onBack={() => setView("amount")}
          onChoose={handleChooseMethod}
          amount={amount}
        />
        <MockProcessingModal
          visible={mockModal.visible}
          method={mockModal.method}
          amount={amount}
          onSuccess={handleMockSuccess}
          onCancel={() => setMockModal({ visible: false, method: null })}
        />
      </>
    );
  }

  if (view === "upi") {
    return (
      <UpiScreen
        onBack={() => setView("menu")}
        amount={amount}
        onSuccess={onBack}
      />
    );
  }

  if (view === "addCardForm") {
    return (
      <AddCreditCardForm
        onBack={() => setView("creditcard")}
        onCardAdded={handleCardAdded}
      />
    );
  }

  if (view === "creditcard") {
    return (
      <AddCreditCardScreen
        onBack={() => setView("menu")}
        onAddCard={() => setView("addCardForm")}
        cards={cards}
        onDeleteCard={handleDeleteCard}
        onSelectCard={handleCardSelect}
      />
    );
  }

  return null;
};

export default WalletTopUp;